import { defineStore } from 'pinia'
import { jwtDecode } from 'jwt-decode'

import AuthService from '../services/auth'
import { useAuthStore } from './auth'
import {
  DONATION_DEFAULT_TRIGGER_MS,
  DONATION_FIRST_SEEN_STORAGE_KEY,
  DONATION_LAST_PROMPT_STORAGE_KEY,
  DONATION_PROMPT_COOLDOWN_MS,
  DONATION_PROMPT_LOCK_TIMEOUT_MS,
  type DonationPromptTrigger,
} from '../constants/donationPrompt'

interface DonationPromptState {
  firstSeenAt: number | null
  lastDonationPromptAt: number | null
  locked: boolean
  pendingDefaultBanner: boolean
  pendingStreakInvite: boolean
}

function readStoredNumber(key: string): number | null {
  const raw = localStorage.getItem(key)
  if (!raw) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

function writeStoredNumber(key: string, value: number) {
  localStorage.setItem(key, String(value))
}

export const useDonationPromptStore = defineStore('donationPrompt', {
  state: (): DonationPromptState => ({
    firstSeenAt: readStoredNumber(DONATION_FIRST_SEEN_STORAGE_KEY),
    lastDonationPromptAt: readStoredNumber(DONATION_LAST_PROMPT_STORAGE_KEY),
    locked: false,
    pendingDefaultBanner: false,
    pendingStreakInvite: false,
  }),

  actions: {
    ensureFirstSeen() {
      if (this.firstSeenAt != null) return
      const now = Date.now()
      this.firstSeenAt = now
      writeStoredNumber(DONATION_FIRST_SEEN_STORAGE_KEY, now)
    },

    // Re-read device storage before each evaluation (keeps memory in sync with localStorage)
    hydrateFromLocalStorage() {
      this.firstSeenAt = readStoredNumber(DONATION_FIRST_SEEN_STORAGE_KEY)
      this.lastDonationPromptAt = readStoredNumber(DONATION_LAST_PROMPT_STORAGE_KEY)
    },

    decodeRegisteredJwt(): Record<string, unknown> | null {
      const authStore = useAuthStore()
      if (authStore.userType !== 'registered' || !authStore.accessToken) {
        return null
      }

      try {
        return jwtDecode(authStore.accessToken) as Record<string, unknown>
      } catch {
        return null
      }
    },

    // Actual prompt timestamp from JWT (only present after a real invite was persisted)
    getJwtLastPromptAt(): number | null {
      const decoded = this.decodeRegisteredJwt()
      if (!decoded || typeof decoded.lastDonationPromptAt !== 'number') {
        return null
      }
      return decoded.lastDonationPromptAt
    },

    getJwtCreatedAt(): number | null {
      const decoded = this.decodeRegisteredJwt()
      if (!decoded || typeof decoded.createdAt !== 'number') {
        return null
      }
      return decoded.createdAt
    },

    // Old registered JWTs lack createdAt; wait for refresh before showing anything
    hasCompatibleRegisteredJwt(): boolean {
      const authStore = useAuthStore()
      if (authStore.userType !== 'registered') return true
      return this.getJwtCreatedAt() != null
    },

    syncFromJwt() {
      const claim = this.getJwtLastPromptAt()
      if (claim == null) return
      if (this.lastDonationPromptAt == null || claim > this.lastDonationPromptAt) {
        this.lastDonationPromptAt = claim
        writeStoredNumber(DONATION_LAST_PROMPT_STORAGE_KEY, claim)
      }
    },

    getEffectiveLastPromptAt(): number | null {
      this.syncFromJwt()
      const local = this.lastDonationPromptAt
      const claim = this.getJwtLastPromptAt()
      if (local == null) return claim
      if (claim == null) return local
      return Math.max(local, claim)
    },

    // Guest: first app entry. Registered: account creation from JWT.
    getAccountBaseline(): number | null {
      this.ensureFirstSeen()
      const authStore = useAuthStore()

      if (authStore.userType === 'guest') {
        return this.firstSeenAt
      }

      if (authStore.userType === 'registered') {
        return this.getJwtCreatedAt()
      }

      return null
    },

    // Cooldown clock: last invite if any, otherwise account/first-seen baseline
    getCooldownBaseline(): number | null {
      return this.getEffectiveLastPromptAt() ?? this.getAccountBaseline()
    },

    isCooldownElapsed(): boolean {
      const baseline = this.getCooldownBaseline()
      if (baseline == null) return false
      return Date.now() - baseline >= DONATION_PROMPT_COOLDOWN_MS
    },

    canShow(trigger: DonationPromptTrigger = 'export'): boolean {
      const authStore = useAuthStore()
      if (authStore.userType === 'non-authenticated') return false

      this.hydrateFromLocalStorage()

      if (authStore.userType === 'registered' && !this.hasCompatibleRegisteredJwt()) {
        return false
      }

      this.syncFromJwt()

      // New users must wait at least one cooldown from account/first-seen
      if (!this.isCooldownElapsed()) return false

      if (trigger === 'default') {
        const baseline = this.getCooldownBaseline()
        if (baseline == null) return false
        return Date.now() - baseline >= DONATION_DEFAULT_TRIGGER_MS
      }

      return true
    },

    async tryShow(trigger: DonationPromptTrigger): Promise<boolean> {
      if (this.locked) return false

      this.locked = true
      const lockTimer = window.setTimeout(() => {
        this.locked = false
      }, DONATION_PROMPT_LOCK_TIMEOUT_MS)

      try {
        this.hydrateFromLocalStorage()
        this.syncFromJwt()
        if (!this.canShow(trigger)) return false

        const now = Date.now()
        this.lastDonationPromptAt = now
        writeStoredNumber(DONATION_LAST_PROMPT_STORAGE_KEY, now)

        const authStore = useAuthStore()
        if (authStore.userType === 'registered') {
          // Fire-and-forget; local state already advanced
          AuthService.patchDonationPrompt({ lastDonationPromptAt: now }).catch((error) => {
            console.error('Failed to sync donation prompt timestamp', error)
          })
        }

        return true
      } finally {
        window.clearTimeout(lockTimer)
        this.locked = false
      }
    },

    async syncToBackendIfNeeded() {
      const authStore = useAuthStore()
      if (authStore.userType !== 'registered') return

      this.hydrateFromLocalStorage()
      this.syncFromJwt()
      const local = this.lastDonationPromptAt
      if (local == null) return

      try {
        const response = await AuthService.patchDonationPrompt({
          lastDonationPromptAt: local,
        })
        const serverValue = response?.data?.lastDonationPromptAt
        if (typeof serverValue === 'number') {
          if (this.lastDonationPromptAt == null || serverValue > this.lastDonationPromptAt) {
            this.lastDonationPromptAt = serverValue
            writeStoredNumber(DONATION_LAST_PROMPT_STORAGE_KEY, serverValue)
          }
        }
      } catch (error) {
        console.error('Failed to migrate donation prompt timestamp', error)
      }
    },

    dismissDefaultBanner() {
      this.pendingDefaultBanner = false
    },

    dismissStreakInvite() {
      this.pendingStreakInvite = false
    },
  },
})
